import { forwardRef, type ReactNode } from 'react';
import { Popover } from '@mui/material';
import type { PopoverProps } from '@mui/material';

/**
 * rw-ds-overlays §14 Toggletip — the interactive counterpart to the tooltip: a
 * click-triggered disclosure that may hold interactive content (a button, a link).
 * Composes `.rw-popover .rw-toggletip` (light surface, shadow-md, caret pointing up
 * at the trigger). Controlled (open/anchorEl/onClose) — the trigger itself is left
 * to the consumer (an info icon button per spec, or any element that already carries
 * its own click affordance). MUI-BRIDGE §Toggletip: MUI `Popover`, never `Tooltip`
 * (that's the non-interactive family member).
 *
 * Focus-into-panel-on-open and focus-back-to-trigger-on-close are Popover's own
 * defaults (autoFocus/restoreFocus); Esc and outside-click both close via `onClose` —
 * never suppress `backdropClick` here (the disclosure pattern requires both).
 */
export interface RwToggletipProps {
  open: boolean;
  anchorEl: PopoverProps['anchorEl'];
  onClose: () => void;
  children: ReactNode;
  id?: string;
  className?: string;
  [dataAttr: `data-${string}`]: string | undefined;
}

const RwToggletip = forwardRef<HTMLDivElement, RwToggletipProps>(function RwToggletip(
  { open, anchorEl, onClose, children, id, className, ...rest },
  ref
) {
  return (
    <Popover
      ref={ref}
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={(_event, _reason) => onClose()}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          className: ['rw-popover', 'rw-toggletip', className].filter(Boolean).join(' '),
          sx: { mt: 'var(--size-4)' }
        }
      }}
      {...rest}
    >
      {children}
    </Popover>
  );
});

export default RwToggletip;
